import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { InjectedFormProps, change, formValueSelector, reduxForm } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';

import { userSelector } from '../../reducers/current-user-selectors';

import { JobFieldDescription } from '../../job/fields/job-field-description';
import { FormattedMessageBody } from '../../messages/legacy-components/formatted-message-body';
import { UiButton } from '../../ui/ui-button';

import { JobFlowButtons } from './job-flow-buttons';

import { createAiJobDescriptionLog } from '../../ai-job-description-log/utils/create-ai-job-description-log';
import {
  GenerateJobDescriptionResponse,
  updateJobDescription,
} from '../../ai-job-description-log/utils/generate-job-description';
import { updateAiJobDescriptionLog } from '../../ai-job-description-log/utils/update-ai-job-description-log';
import { AiAutoFixIcon, ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { ScrollToBottom } from '../../widgets/scroll-to-bottom';

const FORM_NAME = 'jobFlowDescription';

const mapStateToProps = (state: any, ownProps: InputProps) => {
  const selector = formValueSelector(FORM_NAME);
  return {
    description: selector(state, 'description'),
    initialValues: ownProps.job,
    user: userSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    setDescription: (description: string) =>
      dispatch(change(FORM_NAME, 'description', description)),
  },
});

interface InputProps extends JobFlowStep {
  job: Partial<Job>;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type JobFlowDescriptionComponentProps = InputProps & StateProps & DispatchProps;
type JobFlowDescriptionFields = Partial<Job>;
type Props = InjectedFormProps<JobFlowDescriptionFields, JobFlowDescriptionComponentProps> &
  JobFlowDescriptionComponentProps;

type State = {
  suggestedDescription: string;
  explanations: string;
  isLoading: boolean;
  errorMessage: string;
  aiJobDescriptionLog?: AiJobDescriptionLog;
};

class JobFlowDescriptionComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      suggestedDescription: '',
      explanations: '',
      isLoading: false,
      errorMessage: '',
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.dirty !== this.props.dirty) {
      this.props.onDirty(this.props.dirty);
    }
  }

  submit = (job: JobFlowDescriptionFields) => {
    // TODO[PROD-2866]: handle edited case
    this.props.onNext(job);
  };

  handleGenerationData = (response: GenerateJobDescriptionResponse) =>
    this.setState({
      ...response,
    });

  handleGenerationError = (errorMessage: string) =>
    this.setState({
      suggestedDescription: '',
      explanations: '',
      isLoading: false,
      errorMessage,
    });

  handleGenerationSuccess = (aiJobDescriptionLog: AiJobDescriptionLog) => {
    this.setState({ aiJobDescriptionLog, isLoading: false });
  };

  handleSuggestImprovementsClick = async () => {
    this.setState({
      isLoading: true,
      errorMessage: '',
    });

    const jobId = this.props.job.job_id;
    if (!jobId) {
      return;
    }

    const log = await createAiJobDescriptionLog(jobId);
    this.setState({
      aiJobDescriptionLog: log,
    });

    const aiJobDescriptionLogId = this.state.aiJobDescriptionLog?.aiJobDescriptionLogId;
    if (!aiJobDescriptionLogId) {
      return;
    }

    await updateJobDescription({
      aiJobDescriptionLogId,
      description: this.props.description,
      onData: this.handleGenerationData,
      onError: this.handleGenerationError,
      onSuccess: this.handleGenerationSuccess,
    });
  };

  handleUseClick = () => {
    this.props.actions.setDescription(this.state.suggestedDescription);
    this.setState({
      suggestedDescription: '',
      explanations: '',
    });

    const { aiJobDescriptionLog } = this.state;

    if (aiJobDescriptionLog) {
      const newAiJobDescriptionLog = { ...aiJobDescriptionLog, usageStatus: 'USED' } as const;
      this.setState({
        aiJobDescriptionLog: newAiJobDescriptionLog,
      });
      updateAiJobDescriptionLog(newAiJobDescriptionLog);
    }
  };

  render() {
    const { onPrev, onSkip, saving, editing, handleSubmit, description } = this.props;
    const { suggestedDescription, explanations, errorMessage, isLoading } = this.state;
    const buttonDisabled = !description;

    return (
      <form className="" onSubmit={handleSubmit(this.submit)}>
        <JobFieldDescription />
        {suggestedDescription && (
          <Paper className="u-padding-lg">
            <div className="typo-label">Suggested Improvements</div>
            <Paper className="u-margin-top" variant="outlined">
              <ScrollToBottom
                className="u-margin u-overflowY-auto u-maxHeight-200px u-wordBreak-breakWord"
                disabled={!!explanations}
              >
                <FormattedMessageBody messageBody={suggestedDescription} includesHTML />
              </ScrollToBottom>
            </Paper>
          </Paper>
        )}
        {explanations && (
          <Accordion className="u-margin-top">
            <AccordionSummary expandIcon={<MaterialDesignIcon iconName={ICON_NAMES.EXPAND_MORE} />}>
              <div className="typo-label">Explanation of Suggestions</div>
            </AccordionSummary>
            <AccordionDetails className="u-padding-none">
              <ScrollToBottom className="u-overflowY-auto u-maxHeight-200px u-wordBreak-breakWord">
                <FormattedMessageBody messageBody={explanations} includesHTML />
              </ScrollToBottom>
            </AccordionDetails>
          </Accordion>
        )}
        <div className="u-display-flex u-flexDirection-column">
          {/* Since we're wrapping the JobFlowButtons in a div here (to add some
            more inline buttons) we also add a negative margin-top to counteract
          the JobFlowButtons' margin-top */}
          <div className="u-display-flex u-justifyContent-flexEnd" style={{ marginTop: '-15px' }}>
            <div className="u-margin-top-2x">
              {!suggestedDescription ? (
                // TODO[PROD-2811]: determine how to render this after improvements have been suggested and used
                <Tooltip
                  title={buttonDisabled ? 'Save the job with a description to see suggestions' : ''}
                >
                  <div>
                    <UiButton
                      className={`u-flex-center u-gap-10px ${
                        isLoading || buttonDisabled ? '' : 'u-background-darkPurple'
                      }`}
                      onClick={this.handleSuggestImprovementsClick}
                      loading={isLoading}
                      disabled={buttonDisabled}
                      startIcon={<AiAutoFixIcon />}
                    >
                      Suggest Improvements
                    </UiButton>
                  </div>
                </Tooltip>
              ) : (
                <UiButton
                  className="u-margin-left"
                  variant="secondary"
                  onClick={this.handleUseClick}
                  loading={isLoading}
                  smartlookEvent={{
                    eventName: 'Job description improvement click',
                    props: {
                      jobId: this.props.job.job_id || '',
                      user: this.props.user.username,
                    },
                  }}
                >
                  Use Description
                </UiButton>
              )}
            </div>
            <JobFlowButtons
              disabled={buttonDisabled}
              onPrev={onPrev}
              onSkip={onSkip}
              editing={editing}
              saving={saving}
            />
          </div>
          {errorMessage && (
            <div>
              <p className="u-color-red u-textAlign-right">{errorMessage}</p>
            </div>
          )}
        </div>
      </form>
    );
  }
}

export const JobFlowDescription = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<JobFlowDescriptionFields, JobFlowDescriptionComponentProps>({
    form: FORM_NAME,
  })(JobFlowDescriptionComponent),
);
